/**
 * The placeholder shown until a creator uploads their own logo.
 *
 * ── Drawn, not traced ───────────────────────────────────────────────────────
 * Nothing here comes from a stock asset: a glyph built from a rounded rect, a
 * circle and a triangle, plus one line of type. No third-party artwork on a
 * customer's membership, nothing to license.
 *
 * ── Why an svg with <text> ──────────────────────────────────────────────────
 * The wordmark is FIXED ("YOUR LOGO"), unlike an earlier version that set the
 * membership's own name — so there is no unbounded string to lay out, and the
 * box can be sized to the type exactly. Inlined
 * rather than referenced as a file, so `currentColor`,
 * `var(--brand-primary)` and the page's webfont all reach it; through
 * <img src> none of them would.
 *
 * ── Colour ──────────────────────────────────────────────────────────────────
 * The diamond and the word "YOUR" take `--brand-primary`, published by
 * applyFont.js already mode-corrected, so a near-black brand colour is lifted
 * to stay visible on a dark surface. "LOGO" takes `currentColor` and follows
 * the theme like any other text. The grey fallback covers the moment before
 * getfunnel lands, and any surface that never publishes the variable.
 *
 * public/images/brand-default.svg is the same drawing with the colours baked
 * in, for the places that can only take a URL — the course-landing templates
 * and the admin's Brand preview. Keep the two in step.
 */
const BRAND = "var(--brand-primary, #9AA3AE)";
const FONT = "var(--community-font, 'Open Sans', system-ui, -apple-system, sans-serif)";

export default function BrandDefaultMark({ community, className = "", title = "", onClick }) {
  const label = String(community?.name || title || "").trim();

  return (
    <svg
      viewBox="0 0 116 22"
      className={`${className} ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
      fill="none"
      role="img"
      aria-label={label ? `${label} — no logo set` : "No logo set"}
      focusable="false"
    >
      {/*
        An image glyph, then the wordmark on one line.

        The glyph is the only mark here that MEANS something — it says a picture
        belongs in this spot, which is what the placeholder is for. An abstract
        shape would balance the lockup just as well but would read as a logo the
        creator never chose.

        It costs type size: the callers cap at max-w-[130px], so a wider box
        renders shorter — 19.3px of type without the glyph, 15.5px with it.
      */}
      <g stroke={BRAND} fill="none" strokeWidth="1.6">
        <rect x="1.5" y="3" width="16" height="16" rx="4" />
      </g>
      <circle cx="6.2" cy="7.6" r="1.5" fill={BRAND} />
      <path d="M4 16 8.4 10.4 15 16Z" fill={BRAND} />
      {/*
        Centred in the space beside the glyph, not in the whole box — otherwise
        the glyph pushes the words right of centre.

        Anchored to the middle rather than to a left edge: the wordmark is set
        in var(--community-font), so its width varies per membership. Anchoring
        left meant all the width headroom sat on one side and the mark sat off
        centre, which is how it looked wrong on the login card.

        Box width and text centre together set the gap after the glyph. It has
        been tuned several times against how it looks on the login card, and
        landed tight: 116/67.8 gives ~5px at header size and ~8.6px at the
        login's 42px. Earlier passes at 12.6px and 10.9px both read as too far
        apart. A font as wide as DejaVu Bold clips its last few units; the stock
        Open Sans and anything near its metrics fits.
      */}
      <text
        x="67.8"
        y="11"
        fontSize="15"
        fontWeight="700"
        fontFamily={FONT}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        <tspan fill={BRAND}>YOUR </tspan>
        <tspan fill="currentColor">LOGO</tspan>
      </text>
    </svg>
  );
}
